import api_communication from "./api_communication"
import metadata, {
  heroesNames,
  itemNames,
  ensureMetadata,
  getHeroName as getHeroNameById,
  getHeroIdByName,
  getItemName as getItemNameById,
} from "./service/metadata"
import {
  TEAM as team,
  normalizeMatch,
  normalizeMatches,
  getMatchAverageMmr,
} from "./service/matchnormalization"


const tierOneItems = [
  'Boots',
  'NoviceStaff',
  'WoodenSword',
  'WoodenBow',
  'WoodenRing',
  'Buckler',
  'IronRing',
  'Shirt',
  'HealingPotion',
  'ObserverWard',
  'CorruptedPotion',
  'PotionOfMagic',
  'VileFlask',
  'WardRed',
  'Ward',
  'PotionOfVitality',
  'consumablePlaceholderpng',
  'CharmingPendant',
  'HuntersCloak',
]

const tierTwoItems = [
  'LightSlayer',
  'Katana',
  'IronSword',
  'RecklessLongbow',
  'PoisonBow',
  'IronBuckler',
  'ShieldOfDefiance',
  'EnduringShield',
  'WizardStaff',
  'IronRod',
  'RodofNature',
  'RingofRegeneration',
  'RingofTime',
  'IronPendant',
  'LeatherArmor',
  'IronArmor',
  'MagicalShield',
  'EnchantedShield',
  'RodOfNature',
  'ApprenticeArmor',
  'RingOfTime',
  'RingOfPower',
  'DivineKatana'
]

// Hero and item names live in the metadata store; `heroesNames` / `itemNames`
// are the store's own array / Map instances, filled in place, so
// `utils.heroesNames` keeps working for views that read it directly.

// Starts loading hero AND item names now, so independent routes (match,
// player, leaderboard) have them as early as possible. Resolves once both have
// loaded or given up; it never rejects and never hangs. Consumers that need to
// know whether it worked can inspect the resolved status or call
// `ensureMetadata()` again to retry the failed pieces.
const ready = ensureMetadata()

const getHeroName = getHeroNameById

const normalizationHelpers = {
  getHeroName: getHeroNameById,
  getItemName: getItemNameById,
}

// Normalizes a decoded match in place (no clone) and returns it. Safe to call
// on an already-normalized record.
const prettyMatch = match => normalizeMatch(match, normalizationHelpers)

// Page-sized requests (match history, player history). The bulk 25k sample
// goes through MatchesCache, which normalizes in yielding chunks instead.
const getMatches = async (numberOfMatches, playerID = null, version = null, isRanked = false, offset = 0) => {
  const [matches] = await Promise.all([
    api_communication.getMatches(numberOfMatches, playerID, version, isRanked, offset),
    ensureMetadata(),
  ])
  return normalizeMatches(matches, normalizationHelpers)
}

const getMatch = async (matchID) => {
  const [match] = await Promise.all([api_communication.getMatch(matchID), ensureMetadata()])
  if (!match) {
    return
  }
  return prettyMatch(match)
}

const sortObjectByValue = obj => {
  let sorted = {}
  Object.keys(obj).sort((a, b) => {
    return obj[b] - obj[a]
  }).forEach(key => {
    sorted[key] = obj[key]
  })
  return sorted
}

const getPlayersByTeam = (team, match) => {
  let players = []
  match["MatchData"].forEach(player => {
    if (player["team"] === team) {
      players.push(player)
    }
  })
  return players
}

const getHerosByTeam = (team, match) => {
  let heros = []
  getPlayersByTeam(team, match).forEach(player => {
    heros.push(player["heroId"])
  })
  return heros
}

const getAllPlayers = match => {
  return match["MatchData"]
}


const getPlayerByName = (playerName, match) => {
  let players = getAllPlayers(match)
  for (let player of players) {
    if (player["player_name"] == playerName) {
      return player
    }
  }
  return null
}

const getPlayerById = (playerId) => api_communication.getPlayer(playerId)

const getMostPlayedHero = player => {
  try {
    let mostPlayed_counter = 0
    let mostPlayed = null

    const heroes = player["AccountHeroStats"]
    heroes.forEach(hero => {
      if (hero["gamesTotal"] > mostPlayed_counter) {
        mostPlayed_counter = hero["gamesTotal"]
        mostPlayed = hero
      }
    })

    return mostPlayed
  } catch (e) {
    console.log(e)
  }
}

const getItemsUsedAmount = (matches = null) => {
  const items = {}
  matches.forEach(match => {
    getAllPlayers(match).forEach(player => {
      player["items"].forEach(item => {
        if (items[item]) {
          items[item]++
        } else {
          items[item] = 1
        }
      })
    })
  })
  return sortObjectByValue(items)
}

const calculatePoints = (heroDetails) =>
  ((heroDetails.kda * 0.5 +
    heroDetails.winrate * 0.25 +
    heroDetails.towerDamage * 0.1 +
    heroDetails.heroDamage * 0.1 +
    heroDetails.damageTaken * 0.1 +
    heroDetails.networth * 0.1) /
    heroDetails.games).toFixed(1)


const secondsToMinutes = seconds => {
  return Math.floor(seconds / 60) + ':' + (seconds % 60).toString().padStart(2, '0')
}

const normalizeVersionUpdate = (update) => {
  const rawUpdate = String(update ?? '').trim()
  const normalizedUpdate = rawUpdate.length > 1 ? rawUpdate.slice(0, -1) : rawUpdate
  return Number(normalizedUpdate) || 0
}

// Version strings repeat across tens of thousands of matches but only a
// handful of distinct values exist, so parsed tuples are memoized. The bucket
// semantics (major.minor.update with the trailing hotfix character dropped)
// are unchanged.
const parsedVersions = new Map()

const parseVersion = (v) => {
  const key = String(v || '')
  const cached = parsedVersions.get(key)
  if (cached) return cached

  const parts = key.split('.')
  const major = Number(parts[0]) || 0
  const minor = Number(parts[1]) || 0
  const update = normalizeVersionUpdate(parts[2])
  const parsed = [major, minor, update]

  if (parsedVersions.size < 4096) parsedVersions.set(key, parsed)
  return parsed
}

const getVersionBucket = (v) => parseVersion(v).join('.')

const isVersionGreaterOrEqual = (v1, v2) => {
  const [major1 = 0, minor1 = 0, update1 = 0] = parseVersion(v1)
  const [major2 = 0, minor2 = 0, update2 = 0] = parseVersion(v2)

  if (major1 > major2) return true
  if (major1 < major2) return false

  if (minor1 > minor2) return true
  if (minor1 < minor2) return false

  return update1 >= update2
}

const isVersionEqual = (v1, v2) => {
  const [major1 = 0, minor1 = 0, update1 = 0] = parseVersion(v1)
  const [major2 = 0, minor2 = 0, update2 = 0] = parseVersion(v2)

  return major1 === major2 && minor1 === minor2 && update1 === update2
}

const calculateBayesianScore = (winrate, games, k = 25) => {
  const winFraction = winrate / 100
  return 2 * (winFraction * games + 0.45 * k) / (games + k)
}

// Wilson score lower bound: ranks a high winrate on a small sample below a
// more moderate winrate backed by many games, instead of letting a lucky
// few-game streak outscore a well-tested item.
const calculateWilsonScore = (winrate, games, z = 2.5) => {
  if (!games) return 0
  const phat = winrate / 100
  const zSquared = z * z
  const numerator = phat + zSquared / (2 * games) -
    z * Math.sqrt((phat * (1 - phat) + zSquared / (4 * games)) / games)
  const denominator = 1 + zSquared / games
  return numerator / denominator
}

const getWinrateColor = (winrate) => {
  const wr = Math.max(0, Math.min(100, Number(winrate) || 0))
  // Map 0-100 to 0-120 (hue)
  // 0 is red, 60 is yellow, 120 is green
  const hue = (wr * 1.2).toFixed(0)
  return `hsl(${hue}, 80%, 45%)`
}

/**
 * @deprecated Use MatchesCache.getVersionOptions() instead.
 * Returns an empty array – version options are now derived dynamically from the cache.
 */
const generateVersionOptions = () => []


export default {
  ready,
  ensureMetadata,
  metadataState: metadata.state,
  tierOneItems,
  tierTwoItems,
  heroesNames,
  getHeroName,
  itemNames,
  team,
  getMatches,
  getMatch,
  prettyMatch,
  getHerosByTeam,
  getHeroNameById,
  getHeroIdByName,
  getItemNameById,
  getPlayersByTeam,
  getAllPlayers,
  getMatchAverageMmr,
  getPlayerByName,
  getMostPlayedHero,
  getItemsUsedAmount,
  calculatePoints,
  getPlayerById,
  secondsToMinutes,
  isVersionGreaterOrEqual,
  isVersionEqual,
  getVersionBucket,
  calculateBayesianScore,
  calculateWilsonScore,
  getWinrateColor,
  generateVersionOptions,
}
